import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIsSmallViewport } from "hooks/useIsSmallViewport";
import {
  faArrowDownAZ,
  faArrowDownShortWide,
  faArrowUpAZ,
  faArrowUpShortWide,
  faSave,
} from "@fortawesome/free-solid-svg-icons";
import {
  ItemMenu,
  ItemMenuData,
  MultiSelectAction,
} from "components/UI/ItemMenu/ItemMenu";
import { SortByPicker, SortingOptions } from "components/UI/SortByPicker";
import { saveAs } from "file-saver";
import { useGenericModals } from "hooks/useGenericModals";
import { useUserTracks } from "hooks/useUserTracks";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { updateNonSaveableOptions } from "stores/optionsStore/actions";
import { useOptions } from "stores/optionsStore/OptionsContext";
import { getEditedTrackData } from "stores/routingStore/getTrackData";
import { useRouting } from "stores/routingStore/RoutingContext";
import { formatDistanceLabel } from "utils/formatters/formatDistance";
import { encodeGPX } from "utils/gpx/encodeGPX";
import { getSafeName } from "utils/helpers/exportGPX/getExportSafeName";
import { filterDataByString } from "utils/helpers/filterData";
import { showTrackExportSuccessNotification } from "utils/notifications/trackNotifications";
import { useGenericDispatch } from "stores/genericStore/GenericContext";
import { rewriteChartTooltipData } from "stores/genericStore/actions";
import TracksWaypointsMenuPlaceholder from "./TracksWaypointsMenuPlaceholder";
import { Text } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { rewriteTrackmeta } from "stores/routingStore/actions";
import { showErrorNotification } from "utils/notifications/customNotifications";

interface ExtendedItemMenuData extends ItemMenuData {
  length: number;
}

export type TracksSortByOptions = "createdDate" | "name" | "length";

const getSortingOptions = (
  t: TFunction
): SortingOptions<TracksSortByOptions>[] => [
  {
    value: "name",
    label: t("sorting.name.AZ"),
    icon: faArrowDownAZ,
    direction: "asc",
  },
  {
    value: "name",
    label: t("sorting.name.ZA"),
    icon: faArrowUpAZ,
    direction: "desc",
  },
  {
    value: "createdDate",
    label: t("sorting.date.from_latest"),
    icon: faArrowDownShortWide,
    direction: "desc",
  },
  {
    value: "createdDate",
    label: t("sorting.date.from_oldest"),
    icon: faArrowUpShortWide,
    direction: "asc",
  },
  {
    value: "length",
    label: t("sorting.length.from_shortest"),
    icon: faArrowDownShortWide,
    direction: "asc",
  },
  {
    value: "length",
    label: t("sorting.length.from_longest"),
    icon: faArrowUpShortWide,
    direction: "desc",
  },
];

type SortDataOptions = {
  sortBy: TracksSortByOptions;
  data: ExtendedItemMenuData[] | undefined;
  ascending: boolean;
};

const sortData = ({ sortBy, data, ascending }: SortDataOptions) => {
  if (!data) return undefined;
  let sorted = data;
  if (sortBy === "name") {
    sorted = [...data].sort((a, b) => a.name.localeCompare(b.name));
  }
  if (sortBy === "length") {
    sorted = [...data].sort((a, b) => a.length - b.length);
  }
  return ascending ? sorted : sorted.reverse();
};

export const TracksMenu = () => {
  const {
    tracks,
    hiddenTrackIds,
    selectedTrack,
    selectTrack,
    updateTrack,
    toggleTrackVisibility,
    isFetching,
    deleteTrack,
    leaveOpenTrack,
  } = useUserTracks();
  const [filterKeyword, setFilterKeyword] = useState("");
  const { t } = useTranslation();
  const {
    dispatch,
    state: { isLeftMenuOpen, units, sortTracks },
    updateSaveable,
  } = useOptions();
  const isFirstSelect = useRef(true);
  const {
    dispatch: dispatchRouting,
    state: {
      routing,
      computedTrackpoints,
      nonRoutableTrackMetadata,
      trackWaypoints,
      elevations,
      trackMeta,
      trackColor,
    },
    openedTrack,
  } = useRouting();
  const { openErrorModal, openConfirmTrackDeleteModal } = useGenericModals();
  const selectedTrackRef = useRef(selectedTrack);
  const tracksRef = useRef(tracks);
  useEffect(() => {
    selectedTrackRef.current = selectedTrack;
  }, [selectedTrack]);
  useEffect(() => {
    tracksRef.current = tracks;
  }, [tracks]);
  const isSmallViewport = useIsSmallViewport();
  const genericDispatch = useGenericDispatch();

  // Currently unused
  // const getTrackById = (id: number) =>
  //   tracksRef.current?.find((value) => value.id === id);

  useEffect(() => {
    if (
      selectedTrackRef.current &&
      trackWaypoints.length > 1 &&
      computedTrackpoints &&
      elevations &&
      !isFirstSelect.current
    ) {
      updateTrack(
        selectedTrackRef.current.id,
        getEditedTrackData(
          {
            routing,
            trackWaypoints,
            computedTrackpoints,
            elevations,
            trackMeta,
            nonRoutableTrackMetadata,
            trackColor,
          },
          openedTrack
        )
      ).catch(() => {
        // Saved after every edit, the id keeps a lost connection from showing a pile of notifications
        showErrorNotification({
          id: "track-autosave-failed",
          title: t("generic.error_title"),
          message: t("error_modal.generic_text"),
        });
      });
    }
    if (selectedTrack) {
      isFirstSelect.current = false;
    }
    // Autosaves the track once its elevations are recomputed after an edit,
    // so it should only run when the elevations change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elevations]);

  const openOrCloseTrack = useCallback(
    async (id: number) => {
      isFirstSelect.current = true;

      if (id !== selectedTrackRef.current?.id) {
        if (isLeftMenuOpen && isSmallViewport) {
          selectTrack(null);
          genericDispatch(rewriteChartTooltipData(null));
          dispatchRouting(
            rewriteTrackmeta({
              totalDistance: undefined,
              trackPointsSpeed: undefined,
              trackPointsTime: undefined,
              totalTime: undefined,
            })
          );

          // We need to await here for a bit to prevent race donwstream
          await new Promise((resolve) => {
            setTimeout(() => resolve(undefined), 10);
          });

          dispatch(updateNonSaveableOptions({ isLeftMenuOpen: false }));
        }
        selectTrack(id);
      } else {
        selectTrack(null);
      }
    },
    [
      dispatch,
      dispatchRouting,
      genericDispatch,
      isLeftMenuOpen,
      isSmallViewport,
      selectTrack,
    ]
  );

  // Both close the open own track, which may have a change that isn't saved yet
  const handleMenuItemSelect = useCallback(
    (id: number) => leaveOpenTrack(() => void openOrCloseTrack(id)),
    [leaveOpenTrack, openOrCloseTrack]
  );
  // Switching the multiselect closes the own track open in the editor, whose item would stay highlighted.
  // A new track being drawn isn't in the list, so it stays (emptying the editor would lose it)
  const handleMultiSelectModeToggle = useCallback(() => {
    if (selectedTrackRef.current) leaveOpenTrack(() => selectTrack(null));
  }, [leaveOpenTrack, selectTrack]);

  const handleMenuItemRightButtonClick = useCallback(
    (id: number) => {
      toggleTrackVisibility(id);
    },
    [toggleTrackVisibility]
  );

  const deleteTracks: MultiSelectAction = useCallback(
    (ids, callback) => {
      openConfirmTrackDeleteModal({
        count: ids.length,
        onConfirm: () => {
          void deleteTrack(ids).then(callback);
        },
      });
    },
    [deleteTrack, openConfirmTrackDeleteModal]
  );

  const exportToGPX: MultiSelectAction = useCallback(
    (ids, callback) => {
      if (!tracksRef.current) return;
      const selectedTracks = tracksRef.current.filter((item) =>
        ids.includes(item.id)
      );
      const encoded = encodeGPX({
        tracks: selectedTracks.map((track) => ({
          description: track.desc,
          name: track.name,
          trackPoints: track.trackPoints,
          trackPointsEle: track.trackPointsEle,
          links: track.links,
          visible: true,
          comment: track.cmt,
          number: track.number,
          type: track.type,
          source: track.src,
        })),
      });
      try {
        if (!encoded) throw new Error();
        const blob = new Blob([encoded], { type: "application/gpx+xml" });
        if (selectedTracks.length > 1) {
          saveAs(blob, "export.gpx");
        } else {
          saveAs(blob, `${getSafeName(selectedTracks[0].name)}.gpx`);
        }
        showTrackExportSuccessNotification({ count: selectedTracks.length });
        callback();
      } catch (_error) {
        openErrorModal({
          title: t("generic.error_title"),
          text: t("errors.encoding_GPX_failed.text"),
        });
      }
    },
    [openErrorModal, t]
  );

  const data = tracks?.map((track) => {
    const formattedDistance = track.meta?.length
      ? formatDistanceLabel(track.meta.length * 1000, units, 1, t)
      : undefined;

    const itemData: ExtendedItemMenuData = {
      ...track,
      bottomInfo: formattedDistance,
      length: track.meta?.length || 0,
      color: track.color,
    };
    return itemData;
  });

  const filteredData = filterDataByString(data, "name", filterKeyword);

  const sortedData = sortData({
    data: filteredData,
    sortBy: sortTracks.type,
    ascending: sortTracks.direction === "asc",
  });

  const SortComponent = useMemo(
    () => (
      <SortByPicker
        data={getSortingOptions(t)}
        value={sortTracks.type}
        sortDirection={sortTracks.direction}
        onItemChange={(value, direction) => {
          updateSaveable({ sortTracks: { type: value, direction } });
        }}
      />
    ),
    [sortTracks.direction, sortTracks.type, t, updateSaveable]
  );

  return useMemo(
    () => (
      <ItemMenu
        data={sortedData}
        onDeleteClick={deleteTracks}
        onExportToGPXclick={exportToGPX}
        onMultiSelectModeOn={handleMultiSelectModeToggle}
        onFilterInput={setFilterKeyword}
        selectedItemId={selectedTrack?.id}
        hiddenItemIds={hiddenTrackIds}
        onItemClick={(id) => void handleMenuItemSelect(id)}
        onItemRightButtonClick={handleMenuItemRightButtonClick}
        loading={isFetching}
        placeholder={
          <TracksWaypointsMenuPlaceholder
            emptyText={
              <Text
                p="md"
                c="dimmed"
                size="sm"
                sx={{ textAlign: "center", userSelect: "none" }}
              >
                {t("tracks_menu.help_text_01")}
                {t("tracks_menu.help_text_02")}
                {t("tracks_menu.help_text_03a")}{" "}
                <FontAwesomeIcon icon={faSave} color="#7950f2" />{" "}
                {t("tracks_menu.help_text_03b")}
                {t("tracks_menu.help_text_04", {
                  saveText: t("generic.save_button_label"),
                })}
              </Text>
            }
          />
        }
        topSubmenuComponent={SortComponent}
      />
    ),
    [
      hiddenTrackIds,
      SortComponent,
      deleteTracks,
      exportToGPX,
      handleMenuItemRightButtonClick,
      handleMenuItemSelect,
      handleMultiSelectModeToggle,
      isFetching,
      selectedTrack?.id,
      sortedData,
      t,
    ]
  );
};
